import type { FieldMissionItem } from '@groupe-reca/contracts'
import { metersBetween, ringAt, type LatLng, type Ring } from '@/features/detection/domain/geometry'
import type { Radii } from '@/features/detection/domain/geometry'
import type { PublicStatus } from '@/features/detection/domain/types'
import { hasCoordinates, itemAddress } from '../domain/fieldText'

// Diagnostic de calibration (TEMPORAIRE, à retirer après l'observation terrain du lot O7) : distances de la tablette
// au drapeau de chaque résidence (le seul point utilisé par le moteur) et à sa pastille d'adresse.

export interface DebugRow {
  itemId: string
  order: number
  address: string
  flag: LatLng | null
  /** Distance tablette → drapeau (m); `null` sans drapeau ou sans position. */
  flagDistanceM: number | null
  /** Distance tablette → pastille d'adresse (m). */
  markerDistanceM: number | null
  /** Écart pastille → drapeau (m) : la pastille n'est jamais le point de détection. */
  flagOffsetM: number | null
  ring: Ring | null
  status: PublicStatus | null
  hits: number
  disarmed: boolean
}

export interface DebugEngine {
  statuses: Record<string, PublicStatus>
  hits: Record<string, number>
  disarmed: readonly string[]
}

export function debugRows(
  items: readonly FieldMissionItem[],
  position: LatLng | null,
  radii: Radii,
  engine: DebugEngine | null,
): DebugRow[] {
  const rows = items.map((item): DebugRow => {
    const flag = item.snapshot.flag ? { lat: item.snapshot.flag.latitude, lng: item.snapshot.flag.longitude } : null
    const marker = hasCoordinates(item.snapshot)
      ? { lat: item.snapshot.latitude ?? 0, lng: item.snapshot.longitude ?? 0 }
      : null
    const flagDistanceM = flag && position ? metersBetween(flag, position) : null
    return {
      itemId: item.id,
      order: item.order,
      address: itemAddress(item.snapshot),
      flag,
      flagDistanceM,
      markerDistanceM: marker && position ? metersBetween(marker, position) : null,
      flagOffsetM: flag && marker ? metersBetween(flag, marker) : null,
      ring: flagDistanceM === null ? null : ringAt(flagDistanceM, radii),
      status: engine?.statuses[item.id] ?? null,
      hits: engine?.hits[item.id] ?? 0,
      disarmed: engine?.disarmed.includes(item.id) ?? false,
    }
  })
  // Les plus proches du drapeau d'abord, puis les résidences sans drapeau ou sans position, dans l'ordre de la route.
  return rows.sort((a, b) => {
    if (a.flagDistanceM !== null && b.flagDistanceM !== null) return a.flagDistanceM - b.flagDistanceM
    if (a.flagDistanceM !== null) return -1
    if (b.flagDistanceM !== null) return 1
    return a.order - b.order
  })
}

// Cap de la carte (TEMPORAIRE, même retrait) : d'où vient l'orientation affichée et pourquoi elle peut ne pas suivre
// le sens de déplacement.

export interface HeadingDebugInput {
  source: 'PATH' | 'MOVEMENT' | 'FROZEN'
  mode: string
  /** Cap appliqué à la carte (degrés, éventuellement déroulé). */
  heading: number
  /** Cap annoncé par l'appareil (degrés); `null` à l'arrêt ou s'il ne le fournit pas. */
  deviceHeading: number | null
  direction: 'FORWARD' | 'BACKWARD' | null
  pathOffsetM: number | null
  snapToleranceM: number | null
  rotation: 'pending' | 'native' | 'css' | null
  following: boolean | null
}

export interface HeadingDebug {
  source: string
  mapHeading: string
  deviceHeading: string
  /** Écart entre le cap de la carte et celui de l'appareil (degrés, signe = sens de rotation manquant). */
  gap: string
  path: string
  direction: string
  rotation: string
  /** Avertissement principal à lire sur le terrain; `null` : rien d'anormal. */
  warning: string | null
}

const sourceText = { PATH: 'tracé', MOVEMENT: 'mouvement (sans accroche au tracé)', FROZEN: 'figé' } as const

const normalized = (degrees: number) => ((degrees % 360) + 360) % 360

export function headingDebug(input: HeadingDebugInput): HeadingDebug {
  const map = normalized(input.heading)
  const device = input.deviceHeading === null ? null : normalized(input.deviceHeading)
  const gapValue = device === null ? null : ((((device - map) % 360) + 540) % 360) - 180
  const offPath =
    input.pathOffsetM !== null && input.snapToleranceM !== null && input.pathOffsetM > input.snapToleranceM
  let warning: string | null = null
  if (input.following === false) warning = 'suivi du tracteur coupé : nord en haut'
  else if (input.direction === 'BACKWARD' && input.source === 'PATH') warning = 'à contresens du tracé : cap inversé'
  else if (offPath) warning = 'hors du tracé : le cap ne suit plus le parcours'
  else if (gapValue !== null && Math.abs(gapValue) > 45) warning = 'cap de la carte très différent de celui de l’appareil'
  return {
    source: `${sourceText[input.source]} · ${input.mode}`,
    mapHeading: `${map.toFixed(0)}°`,
    deviceHeading: device === null ? '—' : `${device.toFixed(0)}°`,
    gap: gapValue === null ? '—' : `${gapValue > 0 ? '+' : ''}${gapValue.toFixed(0)}°`,
    path:
      input.pathOffsetM === null
        ? 'aucun tracé'
        : `${input.pathOffsetM.toFixed(1)} m du tracé (tolérance ${input.snapToleranceM === null ? '?' : input.snapToleranceM.toFixed(0)} m)`,
    direction: input.direction === 'FORWARD' ? 'dans le sens du tracé' : input.direction === 'BACKWARD' ? 'à contresens' : 'sens inconnu',
    rotation:
      input.rotation === null
        ? '—'
        : `${input.rotation === 'native' ? 'native (Google)' : input.rotation === 'css' ? 'CSS (repli)' : 'en attente'}${input.following === false ? ' · suivi coupé' : ''}`,
    warning,
  }
}
